/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import axios from "axios";
import {
    browserSupportsWebAuthn,
    startAuthentication,
    startRegistration,
    WebAuthnAbortService,
    WebAuthnError,
} from "@simplewebauthn/browser";
import { API_OK, API_URL } from "./Constants";

/** Whether this browser can do WebAuthn at all. */
export const passkeysSupported = () => browserSupportsWebAuthn();

/**
 * Aborts a ceremony that is still open, so the screen can get out of "waiting".
 *
 * This exists because a closed system dialog does not always reject. The spec lets the
 * authenticator keep the request open, and Chrome's Google Password Manager flow does
 * exactly that: the bubble goes away, `navigator.credentials.*` stays pending, and the
 * page sits on "waiting for your device" until the ceremony times out - with no way out and
 * nothing said. Every screen that opens a ceremony must therefore be able to end it.
 *
 * Aborting makes the pending promise reject with `AbortError`, which
 * `passkeyErrorMessage` already reads as "the prompt was closed" - so the cancel path
 * and the real cancel path land in the same place.
 */
export const cancelPasskeyCeremony = () => {
    try {
        WebAuthnAbortService.cancelCeremony();
    } catch (e) {
        // Nothing open to cancel. Not a failure - the caller is resetting its own state.
    }
};

/**
 * How long focus must stay on the page before concluding the prompt closed with no
 * answer.
 *
 * The real flow is not one dialog: the authenticator closes and Google's account
 * confirmation opens right after it, or the customer picks a different provider - and
 * between the two the page holds focus for a moment. A short grace cancelled a live
 * ceremony there, so the grace is long, and any fresh `blur` clears it and starts over.
 *
 * Ten seconds is far more than a successful ceremony needs to settle - the credential
 * already exists, all that is left is the upload to the password manager and handing
 * the object to the page - and far below the ceiling.
 */
const FOCUS_GRACE_MS = 10_000;

/**
 * The ceiling: past it the ceremony is aborted whatever happened.
 *
 * Slightly more than the timeout the server puts on the ceremony (four minutes), so the browser
 * gets its chance to reject on its own before we do it for it. This is the last safety
 * net - it guarantees the screen cannot sit forever even when no other signal arrives.
 *
 * Unconditional: the challenge the server issued has expired anyway, so a ceremony that
 * settled after this point would be refused at verification.
 */
const CEREMONY_CAP_MS = 245_000;

/**
 * Runs a ceremony, and ENDS it even when the browser does not.
 *
 * The spec lets an authenticator leave the request open, and that is what happens in
 * practice: in Chrome's Google Password Manager flow the dialog disappears - cancelled,
 * or refused because the key is already registered - and `navigator.credentials.*`
 * stays pending. No rejection, no error, no event to wait for. The screen sits on
 * "waiting for your device" until the time runs out, which reads as a fault although
 * nothing failed.
 *
 * A cancel button is an escape, not an answer: it asks the customer to notice that the
 * system is stuck. What is needed is for the system to notice.
 *
 * Two signals, both facts about the page rather than guesses about the person:
 *
 *   **Focus came back, AND stayed.** The authenticator dialog is system or browser
 *   chrome, so it TAKES focus from the page; its return means it closed. But a dialog
 *   closing is not the end of the flow - it moves between providers, and asks for
 *   account confirmation in a further window - so the inference only holds if focus
 *   also STAYS: any fresh `blur` inside the grace clears the countdown, and when the
 *   countdown does end we check again that the page really holds focus. That second
 *   check is needed because not every window announces itself with a `blur`.
 *
 *   **A time ceiling**, for an authenticator that never moves focus at all.
 *
 * Listening for `focus` only starts after a real `blur`, and that is what prevents a
 * false catch: someone who switched to another window and came back mid-ceremony would
 * have had it cancelled, were it not for the requirement that focus was taken BY the
 * ceremony. If the page never lost focus, no dialog closed and there is nothing to infer.
 *
 * The abort goes through `cancelPasskeyCeremony`, so the promise rejects with
 * `AbortError` - which `passkeyErrorMessage` already reads as "the prompt was closed".
 * There is no second error path.
 */
const withCeremonyWatchdog = async (run) => {
    let settled = false;
    let lostFocus = false;
    let graceTimer = null;

    const disarm = () => {
        if (graceTimer !== null) {
            clearTimeout(graceTimer);
            graceTimer = null;
        }
    };
    /** The page holds focus - so there is no window above it left to wait for. */
    const pageHoldsFocus = () => document.visibilityState === "visible" &&
        (typeof document.hasFocus !== "function" || document.hasFocus());

    const abortIfStillOpen = () => {
        graceTimer = null;
        if (settled || !pageHoldsFocus()) return;
        cancelPasskeyCeremony();
    };
    const onBlur = () => {
        lostFocus = true;
        // Another window opened, so the ceremony is alive: the countdown restarts on return.
        disarm();
    };
    const onFocus = () => {
        if (settled || !lostFocus) return;
        disarm();
        graceTimer = setTimeout(abortIfStillOpen, FOCUS_GRACE_MS);
    };
    // On mobile the page goes hidden when the system sheet opens, without a `blur`.
    const onVisibility = () => {
        if (document.visibilityState === "hidden") onBlur();
        else onFocus();
    };

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const capTimer = setTimeout(() => {
        if (!settled) cancelPasskeyCeremony();
    }, CEREMONY_CAP_MS);

    try {
        return await run();
    } finally {
        settled = true;
        window.removeEventListener("blur", onBlur);
        window.removeEventListener("focus", onFocus);
        document.removeEventListener("visibilitychange", onVisibility);
        disarm();
        clearTimeout(capTimer);
    }
};

/** Signs only the supplied action challenge, without opening a login challenge. */
export const signStepUpOptions = (options) => withCeremonyWatchdog(() => startAuthentication({optionsJSON: options}));

/**
 * The text shown when the browser ceremony fails.
 *
 * A cancelled prompt is not a failure — the user closed a dialog on purpose —
 * and reporting it as one sends them looking for a fault that isn't there.
 * `NotAllowedError` covers both cancel and timeout: the browser deliberately
 * does not distinguish them, so neither do we.
 */
export const passkeyErrorMessage = (error) => {
    const code = error instanceof WebAuthnError ? error.code : null;
    if (code === "ERROR_CEREMONY_ABORTED") {
        return "The passkey prompt was closed. You can try again";
    }
    if (code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
        return "This passkey is already registered on your account";
    }
    if (code === "ERROR_INVALID_DOMAIN" || code === "ERROR_INVALID_RP_ID") {
        return "A passkey cannot be used from this address";
    }

    const name = error?.name;
    if (name === "NotAllowedError" || name === "AbortError") {
        return "The passkey prompt was closed. You can try again";
    }
    if (name === "InvalidStateError") {
        return "This passkey is already registered on your account";
    }
    if (name === "NotSupportedError") {
        return "This browser does not support passkeys";
    }
    if (name === "SecurityError") {
        return "A passkey cannot be used from this address";
    }
    return "The passkey could not be used. Please try again";
};

/**
 * Creates a passkey on this device and enrolls it as a 2FA method.
 *
 * The three steps live together because none of them means anything alone: the
 * options carry a one-time challenge that the ceremony spends, and a ceremony
 * whose result is never posted back leaves a key on the device that the server
 * has never heard of — which is exactly what "my passkey disappeared" is.
 *
 * Returns the raw `/verifyPasskeyRegistration` reply so the caller keeps its own
 * error reporting; browser-side failures are thrown.
 */
export const registerPasskey = async (config) => {
    const optionsReply = await axios.post(API_URL + "getPasskeyRegistrationOptions", {}, config);
    if (optionsReply?.data?.responseStatus !== API_OK || !optionsReply.data.options) {
        return optionsReply;
    }

    const attestation = await withCeremonyWatchdog(() =>
        startRegistration({ optionsJSON: optionsReply.data.options })
    );

    return axios.post(API_URL + "verifyPasskeyRegistration", { response: attestation }, config);
};

/**
 * Signs the server's challenge with one of the account's passkeys.
 *
 * Returns the raw `/verifyPasskeyLogin` reply — the caller lands the session —
 * and throws on a browser-side failure. `rememberDevice` rides along so a
 * passkey login can mark this browser trusted exactly like a code login does.
 */
export const verifyPasskeyLogin = async (config, rememberDevice) => {
    const challengeReply = await axios.post(API_URL + "getPasskeyChallenge", {}, config);
    if (challengeReply?.data?.responseStatus !== API_OK || !challengeReply.data.options) {
        return challengeReply;
    }

    const assertion = await withCeremonyWatchdog(() =>
        startAuthentication({ optionsJSON: challengeReply.data.options })
    );

    return axios.post(
        API_URL + "verifyPasskeyLogin",
        { response: assertion, ...(rememberDevice === true ? { rememberDevice: true } : {}) },
        config
    );
};

/**
 * How a passkey is named in a list of methods.
 *
 * The name the customer gave it first, then the model the server recognised:
 * two passkeys on one account are otherwise the same word twice, and the point
 * of the list is to choose between them. The maker has a column of its own —
 * it is a fact about the key, not a name for it.
 */
export const passkeyDisplayName = (passkey, fallback) => {
    if (!passkey) {
        return fallback;
    }
    return passkey.label || passkey.authenticatorName || fallback;
};

/** The translation key for a passkey's kind, as the server resolved it. */
export const passkeyKindLabel = (passkey) => {
    switch (passkey?.kind) {
        case "cloud":
            return "Synced in the cloud";
        case "manager":
            return "Password manager";
        case "securityKey":
            return "Hardware security key";
        case "platform":
            return "On this device";
        default:
            return "Passkey";
    }
};
